import { ref } from 'vue'
import { useAuthStore } from '../stores/authStore'
import { log } from '../utils/logger'
import { getEnvironment } from '../utils/environment'

// Cache for categories and articles to reduce API calls
const apiCache = {
  categories: {
    data: null as unknown as unknown[] | null,
    timestamp: 0,
    ttl: 5 * 60 * 1000, // 5 minutes cache
  },
  articles: {
    data: null as unknown as unknown[] | null,
    timestamp: 0,
    ttl: 5 * 60 * 1000, // 5 minutes cache
  },
  articleDetails: {} as Record<string, { data: unknown; timestamp: number }>,
}

// Legacy function removed - using centralized environment configuration instead

// Use centralized environment configuration
import { getBaseAPI, getPythonAPI } from '../utils/environment'

// Get correct API base URL based on endpoint type
const getApiBaseUrl = (endpoint: string = '') => {
  // Python backend endpoints
  const isPythonEndpoint = endpoint.includes('/categories/source/') || 
                          endpoint.includes('/crm/')
  
  return isPythonEndpoint ? getPythonAPI() : getBaseAPI()
}

// Clean URL construction - baseUrl + endpoint
const constructCleanApiUrl = (endpoint: string): string => {
  const base = getApiBaseUrl(endpoint)
  const finalUrl = `${base}${endpoint}`

  log.debug('🔗 Clean URL construction:', { endpoint, finalUrl, baseUrl: base })
  return finalUrl
}


export function useApi() {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const authStore = useAuthStore()

  const fetchData = async <T>(endpoint: string, useCache = false): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      // Check cache first for article requests
      if (endpoint.includes('/api/articles/by-slug/')) {
        const slug = endpoint.split('/by-slug/')[1]
        if (slug && apiCache.articleDetails && apiCache.articleDetails[slug]) {
          const cached = apiCache.articleDetails[slug]
          const now = Date.now()
          if (now - cached.timestamp < 5 * 60 * 1000) {
            // 5 minutes cache
            log.debug(`📦 Using cached article data for slug: ${slug}`)
            return cached.data as T
          }
        }
      }

      // Check cache for other requests - DISABLED FOR DEVELOPMENT
      if (useCache) {
        const now = Date.now()

        // Check categories cache - only for full categories, not parents_only
        if (endpoint.includes('/api/categories') && !endpoint.includes('/by-slug/') && !endpoint.includes('parents_only')) {
          if (
            apiCache.categories.data &&
            now - apiCache.categories.timestamp < apiCache.categories.ttl
          ) {
            log.debug('📦 Using cached categories data')
            return apiCache.categories.data as T
          }
        }

        // Check articles cache
        if (endpoint.includes('/api/articles') && !endpoint.includes('/by-slug/')) {
          if (apiCache.articles.data && now - apiCache.articles.timestamp < apiCache.articles.ttl) {
            log.debug('📦 Using cached articles data')
            return apiCache.articles.data as T
          }
        }
      }

      // Centralized URL construction for all environments
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // Centralized EC2-based API for all environments
        fullUrl = constructCleanApiUrl(endpoint)
      }

      // Simple cache-busting for staging
      if (
        window.location.hostname.includes('staging') ||
        window.location.hostname === 'staging.takefav.com'
      ) {
        const separator = fullUrl.includes('?') ? '&' : '?'
        fullUrl += `${separator}_staging=${Date.now()}`
      }

      // Add cache-busting for local development
      if (window.location.hostname === 'localhost') {
        const separator = fullUrl.includes('?') ? '&' : '?'
        fullUrl += `${separator}_dev=${Date.now()}&_local=true&_v=1&_force=${Math.random()}`
      }

      log.api('GET', fullUrl)

      // Prepare headers
      const headers: Record<string, string> = {}

      // Add auth header for admin requests
      if (endpoint.includes('/api/admin/') && authStore.token) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        log.debug('🔐 Adding auth header for admin request')
      }

      // Add auth header for TF (TakeFav) requests
      if (endpoint.includes('/api/tf/') && authStore.token) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        log.debug('🔐 Adding auth header for TF request')
      }

      // Handle auth verify endpoint - append token as query parameter
      if (endpoint.includes('/api/auth/verify') && authStore.token) {
        const separator = fullUrl.includes('?') ? '&' : '?'
        fullUrl += `${separator}token=${encodeURIComponent(authStore.token)}`
        log.debug('🔐 Adding token as query parameter for auth verify request')
      }

      // Longer timeout for product fetching operations (AliExpress can be slow)
      const timeoutDuration = endpoint.includes('/api/products/fetch/') ? 30000 : 7000 // 30s for product fetch, 7s for others

      // api.takefav.com intermittently fails mid-response on larger payloads
      // (QUIC/HTTP2 errors, truncated JSON) — retry those specifically. Real
      // HTTP error responses (4xx/5xx) and timeouts/aborts are NOT retried:
      // they're legitimate outcomes, not network flakiness, and fetchData has
      // no side effects (GET-only) so a retry here is always safe.
      const MAX_ATTEMPTS = 3
      const RETRY_DELAY_MS = 400

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => {
          if (!controller.signal.aborted) {
            controller.abort()
          }
        }, timeoutDuration)

        try {
          const response = await fetch(fullUrl, {
            headers,
            signal: controller.signal,
          })

          clearTimeout(timeoutId)

          if (!response.ok) {
            // Handle missing admin/analytics endpoints in production gracefully
            if (
              response.status === 404 &&
              (endpoint.includes('/api/admin/') || endpoint.includes('/api/analytics/'))
            ) {
              log.warn(`⚠️ Admin/analytics endpoint not available in production: ${endpoint}`)
              return null
            }
            // A real HTTP error response, not a network failure — do not retry.
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const data = await response.json()
          // log.debug('✅ API response received:', {
          //   endpoint,
          //   dataLength: Array.isArray(data) ? data.length : 'object',
          // })

          // Cache data if caching is enabled
          if (useCache) {
            const now = Date.now()

            // Cache categories data with longer TTL (5 minutes) - only for full categories, not parents_only
            if (endpoint.includes('/api/categories') && !endpoint.includes('/by-slug/') && !endpoint.includes('parents_only')) {
              apiCache.categories.data = data
              apiCache.categories.timestamp = now
              log.debug('💾 Cached categories data')
            }

            // Cache articles data
            if (endpoint.includes('/api/articles') && !endpoint.includes('/by-slug/')) {
              apiCache.articles.data = data
              apiCache.articles.timestamp = now
              log.debug('💾 Cached articles data')
            }

            // Cache individual article data (with slug)
            if (endpoint.includes('/api/articles/by-slug/')) {
              const slug = endpoint.split('/by-slug/')[1]
              if (slug && !apiCache.articleDetails) {
                apiCache.articleDetails = {}
              }
              if (slug && apiCache.articleDetails) {
                apiCache.articleDetails[slug] = {
                  data,
                  timestamp: now,
                }
                log.debug(`💾 Cached article data for slug: ${slug}`)
              }
            }
          }

          return data
        } catch (fetchError) {
          clearTimeout(timeoutId)

          const isAbort = fetchError instanceof Error && fetchError.name === 'AbortError'
          const isHttpStatusError =
            fetchError instanceof Error && fetchError.message.startsWith('HTTP error! status:')
          const isRetryable = !isAbort && !isHttpStatusError

          if (isRetryable && attempt < MAX_ATTEMPTS) {
            log.warn(
              `⚠️ Retrying ${endpoint} after transient error (attempt ${attempt}/${MAX_ATTEMPTS}): ${
                fetchError instanceof Error ? fetchError.message : fetchError
              }`
            )
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * attempt))
            continue
          }

          throw fetchError
        }
      }

      // Unreachable: the loop above always returns or throws. Kept only so
      // TypeScript can see every path returns/throws a value.
      throw new Error(`fetchData: exhausted retries for ${endpoint} without a definitive result`)
    } catch (err) {
      // Don't log AbortError as it's expected during navigation
      if (err instanceof Error && err.name === 'AbortError') {
        log.debug('🔄 Request aborted (likely due to navigation):', endpoint)
        return null
      }

      // Handle CORS errors and missing admin endpoints gracefully
      if (
        err instanceof Error &&
        (err.message.includes('CORS') ||
          err.message.includes('Failed to fetch') ||
          err.message.includes('NetworkError')) &&
        (endpoint.includes('/api/admin/') || endpoint.includes('/api/analytics/'))
      ) {
        log.warn(`⚠️ Admin/analytics endpoint not available in production: ${endpoint}`)
        return null
      }

      log.error(`❌ Error fetching from ${endpoint}:`, err)
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          error.value = 'Request timed out. Please try again.'
        } else {
          error.value = err.message
        }
      } else {
        error.value = 'An error occurred while fetching data. Please try again later.'
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const postData = async <T>(endpoint: string, data: unknown): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      // Simple URL construction
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // Route certain CRM endpoints explicitly to Node (local) to avoid Python CORS/auth issues
        const isCrmNodeInsert = endpoint.includes('/api/crm/insert-products')

        // Article generation should go through Node.js backend (which proxies to Python)
        // This ensures proper error handling, logging, and avoids CORS issues
        const shouldUsePython =
          (endpoint.includes('/api/trends/') ||
            endpoint.includes('/api/crm/')) &&
          !isCrmNodeInsert &&
          !endpoint.includes('/api/articles/generate') // Article generation goes through Node.js

        if (shouldUsePython) {
          // Use Python API for trends and CRM fetch-only endpoints (but not article generation)
          fullUrl = `${getPythonAPI()}${endpoint}`
        } else {
          // Article generation and other endpoints go through Node.js backend
          fullUrl = constructCleanApiUrl(endpoint) // Default to Node base
        }
      }

      log.api('POST', fullUrl)
      log.debug('🔍 URL Construction Debug:', {
        endpoint,
        fullUrl,
        environment:
          window.location.hostname === 'takefav.com' ||
          window.location.hostname === 'www.takefav.com'
            ? 'production'
            : 'development',
      })

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Add auth header for admin requests, auth verification, TF, and CRM endpoints
      if (
        (endpoint.includes('/api/admin/') || 
         endpoint.includes('/api/auth/verify') ||
         endpoint.includes('/api/tf/') ||
         endpoint.includes('/api/products/fetch/') ||
         endpoint.includes('/api/crm/')) &&
        authStore.token
      ) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        log.debug('🔐 Adding auth header for protected request')
      }

      const controller = new AbortController()
      // POST /api/articles/generate used to run the whole multi-minute AI
      // pipeline synchronously and needed a 5-minute timeout here to match —
      // pointless, since Cloudflare's own ~100s edge timeout in front of the
      // API killed the connection long before this ever fired anyway (see
      // .claude/skills/article-generation/SKILL.md). It now returns a job_id
      // immediately; poll fetchData('/api/articles/generate/status/:id') for
      // progress, which uses its own short default timeout.
      const timeout = endpoint.includes('/api/products/fetch/')
        ? 30000 // 30 seconds for product fetching (AliExpress can be slow)
        : endpoint.includes('/api/articles/')
          ? 15000
          : endpoint.includes('/api/favorites/')
            ? 15000 // 15 seconds for favorites (batch operations can be slow)
            : 7000 // 7 seconds for others
      const timeoutId = setTimeout(() => controller.abort(), timeout)

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({}) as { error?: string; conflict?: boolean })
        const error = new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        ) as unknown as { message: string; status?: number; conflict?: boolean }
        error.status = response.status
        error.conflict = errorData.conflict
        error.message =
          errorData.message || errorData.error || `HTTP error! status: ${response.status}`
        throw error
      }

      const responseData = await response.json()
      log.debug('✅ POST response received:', {
        endpoint,
        dataLength: Array.isArray(responseData) ? responseData.length : 'object',
      })

      return responseData
    } catch (err) {
      log.error(`❌ Error posting to ${endpoint}:`, err)
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          error.value = 'Request timed out. Please try again.'
        } else {
          error.value = err.message
        }
      } else {
        error.value = 'An error occurred while sending data. Please try again later.'
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const postForgotPassword = async <T>(data: unknown): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      const fullUrl = constructCleanApiUrl('/api/auth/forgot-password')
      log.api('POST', fullUrl)

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorText = await response.text()
        log.error('❌ Forgot password POST request failed:', {
          status: response.status,
          statusText: response.statusText,
          url: fullUrl,
          error: errorText,
        })

        // Try to parse error as JSON
        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { message: errorText || 'Request failed' }
        }

        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      // log.debug('✅ Forgot password POST request successful:', { url: fullUrl, result })
      return result
    } catch (err: unknown) {
      log.error('❌ Error in postForgotPassword:', err)
      error.value = (err as Error).message || 'Failed to send password reset request'
      throw err
    } finally {
      loading.value = false
    }
  }

  const postPythonData = async <T>(endpoint: string, data: unknown): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      // Handle URL construction for different environments
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // In all environments, route via centralized environment configuration
        fullUrl = `${getPythonAPI()}${endpoint}`
      }
      log.api('POST (Python)', fullUrl)

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Attach auth token for CRM Python calls when available (prod will be proxied via /_python)
      if (authStore.token) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        log.debug('🔐 Adding auth header for Python request')
      }

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorText = await response.text()
        log.error('❌ Python API POST request failed:', {
          status: response.status,
          statusText: response.statusText,
          url: fullUrl,
          error: errorText,
        })

        // Try to parse error as JSON
        let errorData
        try {
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { message: errorText || 'Request failed' }
        }

        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      log.debug('✅ Python API POST request successful:', { url: fullUrl, result })
      return result
    } catch (err: unknown) {
      log.error('❌ Error in postPythonData:', err)
      error.value = (err as Error).message || 'Failed to send request to Python API'
      throw err
    } finally {
      loading.value = false
    }
  }

  const putData = async <T>(endpoint: string, data: unknown): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      // Handle URL construction for different environments
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // Centralized EC2-based API for all environments
        fullUrl = constructCleanApiUrl(endpoint)
      }
      log.api('PUT', fullUrl)

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Add auth header for admin requests, auth verification, TF requests, and profile updates
      if (
        (endpoint.includes('/api/admin/') || 
         endpoint.includes('/api/auth/verify') ||
         endpoint.includes('/api/auth/profile') ||
         endpoint.includes('/api/tf/')) &&
        authStore.token
      ) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        log.debug('🔐 Adding auth header for protected request')
      }

      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({}) as { error?: string; conflict?: boolean })
        const error = new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        ) as unknown as { message: string; status?: number; conflict?: boolean }
        error.status = response.status
        error.conflict = errorData.conflict
        error.message =
          errorData.message || errorData.error || `HTTP error! status: ${response.status}`
        throw error
      }

      const responseData = await response.json()
      log.debug('✅ PUT response received:', {
        endpoint,
        dataLength: Array.isArray(responseData) ? responseData.length : 'object',
      })

      return responseData
    } catch (err) {
      log.error(`❌ Error putting to ${endpoint}:`, err)
      if (err instanceof Error) {
        error.value = err.message
      } else {
        error.value = 'An error occurred while updating data. Please try again later.'
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const patchData = async <T>(endpoint: string, data: unknown): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      // Handle URL construction for different environments
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // Centralized EC2-based API for all environments
        fullUrl = constructCleanApiUrl(endpoint)
      }
      log.api('PATCH', fullUrl)

      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      // Add auth header for admin requests and auth verification
      if (
        (endpoint.includes('/api/admin/') || endpoint.includes('/api/auth/verify')) &&
        authStore.token
      ) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        log.debug('🔐 Adding auth header for admin/auth request')
      }

      const response = await fetch(fullUrl, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({}) as { error?: string; conflict?: boolean })
        const error = new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        ) as unknown as { message: string; status?: number; conflict?: boolean }
        error.status = response.status
        error.conflict = errorData.conflict
        error.message =
          errorData.message || errorData.error || `HTTP error! status: ${response.status}`
        throw error
      }

      const responseData = await response.json()
      log.debug('✅ PATCH response received:', {
        endpoint,
        dataLength: Array.isArray(responseData) ? responseData.length : 'object',
      })

      return responseData
    } catch (err) {
      log.error(`❌ Error patching to ${endpoint}:`, err)
      if (err instanceof Error) {
        error.value = err.message
      } else {
        error.value = 'An error occurred while updating data. Please try again later.'
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const fetchPythonData = async <T>(endpoint: string, useCache = false): Promise<T | null> => {
    try {
      loading.value = true
      error.value = null

      // Use Python API base URL for category endpoints - CENTRALIZED environment detection
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // Use centralized environment configuration for ALL environments
        fullUrl = `${getPythonAPI()}${endpoint}`
        log.debug(`🔧 Using Python API for environment: ${getEnvironment()} -> ${getPythonAPI()}`)
      }

      log.api('GET (Python)', fullUrl)

      // Prepare headers
      const headers: Record<string, string> = {}

      // Add auth header for admin requests
      if (endpoint.includes('/api/admin/') && authStore.token) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        // log.debug('🔐 Adding auth header for Python admin request')
      }

      const controller = new AbortController()
      const timeoutId = setTimeout(() => {
        if (!controller.signal.aborted) {
          controller.abort()
        }
      }, 7000) // 7 second timeout

      try {
        const response = await fetch(fullUrl, {
          headers,
          signal: controller.signal,
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        // log.debug('✅ Python API response received:', {
        //   endpoint,
        //   dataLength: Array.isArray(data) ? data.length : 'object',
        // })

        return data
      } catch (fetchError) {
        clearTimeout(timeoutId)
        throw fetchError
      }
    } catch (err) {
      log.error(`❌ Error fetching from Python API ${endpoint}:`, err)
      if (err instanceof Error) {
        error.value = err.message
      } else {
        error.value = 'An error occurred while fetching data from Python API. Please try again later.'
      }
      return null
    } finally {
      loading.value = false
    }
  }

  const deleteData = async (endpoint: string): Promise<boolean> => {
    try {
      loading.value = true
      error.value = null

      // Handle URL construction for different environments
      let fullUrl = endpoint
      if (!endpoint.startsWith('http')) {
        // Centralized EC2-based API for all environments
        fullUrl = constructCleanApiUrl(endpoint)
      }
      log.api('DELETE', fullUrl)

      // Prepare headers
      const headers: Record<string, string> = {}

      // Add auth header for admin requests, auth verification, and TF requests
      if (
        (endpoint.includes('/api/admin/') || 
         endpoint.includes('/api/auth/verify') ||
         endpoint.includes('/api/tf/')) &&
        authStore.token
      ) {
        headers['Authorization'] = `Bearer ${authStore.token}`
        // log.debug('🔐 Adding auth header for protected request')
      }

      const response = await fetch(fullUrl, {
        method: 'DELETE',
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}) as { error?: string })
        const error = new Error(
          errorData.error || `HTTP error! status: ${response.status}`,
        ) as unknown as { message: string; status?: number }
        error.status = response.status
        error.message =
          errorData.message || errorData.error || `HTTP error! status: ${response.status}`
        throw error
      }

      log.debug('✅ DELETE response received:', { endpoint })
      return true
    } catch (err) {
      log.error(`❌ Error deleting from ${endpoint}:`, err)
      if (err instanceof Error) {
        error.value = err.message
      } else {
        error.value = 'An error occurred while deleting data. Please try again later.'
      }
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    fetchData,
    fetchPythonData,
    postData,
    postForgotPassword,
    postPythonData,
    putData,
    patchData,
    deleteData,
  }
}
